import React, { useRef, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import TextBundle from "aldoo-ra/CMS/text-bundle"

/**
 * Search component for Help Center with updated design
 * @param {Object} props
 * @param {Function} props.onResultSelect - Handler for when a search result is selected
 * @param {Object} props.searchState - Search state from useSearch hook
 */
const Search = ({ onResultSelect, searchState, customInputClass }) => {
  const texts = TextBundle({ id: "help-center" })

  const {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    handleSearchChange,
    resetSearch,
    closeResults,
    setShowResults,
  } = searchState

  const searchRef = useRef(null)

  // Close search results if clicked outside
  const handleClickOutside = (e) => {
    if (searchRef.current && !searchRef.current.contains(e.target)) {
      closeResults()
    }
  }

  // Set up event listener for clicks outside
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Handle focus on search input
  const handleFocus = () => {
    if (searchTerm && searchResults.length > 0) {
      setShowResults(true)
    }
  }

  // Handle selection of a search result
  const handleResultClick = (result) => {
    closeResults()
    resetSearch()
    if (onResultSelect) {
      onResultSelect(result)
    }
  }

  return (
    <div
      ref={searchRef}
      className="search-container relative w-full max-w-[604px] lg:max-w-[674px] flex flex-col items-center mx-auto"
      style={{ position: "relative", zIndex: 100 }}
    >
      <div className="relative w-full flex items-center">
        <Input
          type="text"
          variant="mobilingo_big_search"
          placeholder={texts?.search_placeholder || "Search articles"}
          className="w-full h-[56px] md:h-[66px] rounded-full text-xl  shadow-soft_lg"
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
        />

        <button
          className="absolute w-[46px] md:w-14 h-[46px] md:h-14 right-[6px] flex items-center justify-center bg-primary hover:bg-primary_hov text-white rounded-full"
          aria-label="Search"
        >
          <MagnifyingGlassIcon className="w-[30px] h-[30px] text-white"/>
        </button>
      </div>

      {/* Search Results Dropdown */}
      {showResults && searchResults.length > 0 && (
        <div
          className="absolute w-full bg-paper rounded-[20px] shadow-md overflow-hidden p-2"
          style={{
            zIndex: 100,
            position: "absolute",
            top: "100%",
            marginTop: "8px",
            filter: "drop-shadow(0 4px 16px rgba(0, 0, 0, 0.1))",
          }}
        >
          {searchResults.map((result, index) => (
            <div
              key={`${result.type}-${result.item.slug || index}`}
              className="px-4 py-5 hover:bg-gradient_showcase_card rounded-[20px] cursor-pointer"
              onClick={() => handleResultClick(result)}
            >
              <Typography
                variant="body1"
                className="text-text mt-0 mb-4 text-left"
              >
                {result.item.title}
              </Typography>

              <div className="flex items-center text-gray_700 flex-wrap gap-1">
                <Typography 
                  variant="menu"
                  className="!text-gray_700 my-0"
                >
                  {texts?.help_center || "Help Center"}
                </Typography>

                {result.displayPath && result.displayPath.length > 0 && (
                  <>
                    <ChevronRightIcon className="w-4 h-4 md:mr-1" />

                    {result.displayPath.map((pathItem, i) => (
                      <React.Fragment key={`path-${i}`}>
                        <Typography 
                          variant="menu"
                          className="!text-gray_700 my-0"
                        >
                          {pathItem}
                        </Typography>

                        {i < result.displayPath.length - 1 && (
                          <ChevronRightIcon className="w-4 h-4 md:mr-1" />
                        )}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {showResults &&
        searchTerm &&
        searchResults.length === 0 &&
        !isSearching && (
          <div
            className="absolute w-full bg-white border rounded-xl p-6 text-center"
            style={{
              zIndex: 100,
              position: "absolute",
              top: "100%",
              marginTop: "8px",
              filter: "drop-shadow(0 4px 16px rgba(0, 0, 0, 0.1))",
            }}
          >
            <Typography 
              variant="body1_dim"
            >
              {texts?.no_search_results || "No results found"}
            </Typography>
          </div>
        )}
    </div>
  )
}

export default Search
